import { useState } from 'react';
import { createChallenge } from '../lib/challenges';
import { shareLink } from '../lib/share';
import { loadProfile } from '../lib/state';
import { friendOfStory, markFriendStorySent } from '../lib/friend-stories';
import { track } from '../analytics';

export const FRIEND_STORY = 'story-about-a-friend';

/**
 * The send, for A Story About You Two: right where the storyboard has just
 * turned over (owner, 2026-09-30: share after the magic, and ask for that one
 * person by name). The link opens their side of it: your storyboard, your
 * voice, and their turn to tell it back.
 *
 * Your own name is never asked here. It was given once, on Day 2's name tag;
 * a link without one still works and says "A friend".
 *
 * `tone` is the dark stage the storyboard opens on, or the cream sheet of the
 * card screen when there is no storyboard (crew off, or it did not come out).
 */
export function SendStory({ postId, friend, tone = 'stage', drawn = true, onSent }: { postId?: string; friend?: string; tone?: 'stage' | 'sheet'; drawn?: boolean; onSent?: () => void }) {
  const known = friendOfStory(postId);
  const name = (friend || known?.name || '').trim();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(!!known?.sentAt);
  const [note, setNote] = useState<string | null>(null);

  const send = async () => {
    if (!postId) { setNote('One moment: it is still being kept.'); return; }
    setBusy(true);
    setNote(null);
    try {
      const me = loadProfile().name;
      const ch = await createChallenge({ challengeId: FRIEND_STORY, deal: name ? { friend: name } : {}, fromPostId: postId, fromName: me || undefined, toName: name || undefined });
      const text = drawn
        ? `I told a story about us${name ? `, ${name}` : ''}, and my crew drew it. Your turn: tell it from your side.`
        : `I told a story about us${name ? `, ${name}` : ''}. Your turn: tell it from your side.`;
      const outcome = await shareLink(ch.shareUrl, text);
      track('challenge_sent', { outcome, to: name ? 'named' : 'anyone', of: drawn ? 'storyboard' : 'recording' });
      if (outcome === 'cancelled') return;
      if (outcome === 'unsupported') { try { await navigator.clipboard.writeText(ch.shareUrl); } catch { setNote(ch.url); } }
      markFriendStorySent(postId);
      setSent(true);
      setNote(outcome === 'shared' ? null : 'Link copied. Send it to them.');
      onSent?.();
    } catch (err) {
      console.warn(err);
      setNote((err as { reason?: string }).reason || 'Could not make their link. Try again in a moment.');
    } finally { setBusy(false); }
  };

  const who = name || 'them';
  return (
    <div className={`send-story ${tone === 'stage' ? 'on-dark' : 'on-cream'}`}>
      <span className="label">{sent ? `Sent to ${who}` : `${name || 'Your friend'} is in this one`}</span>
      <p className="why">
        {sent
          ? `When ${who} tells it back from their side, it lands next to yours and you two are friends here.`
          : `Send it to ${who}. They ${drawn ? 'see the storyboard, hear you tell it' : 'hear you tell it'}, then tell it back their way. Stories come easier with two people.`}
      </p>
      <button className={`btn ${sent ? 'small ghost' : 'coral'}`} onClick={send} disabled={busy || !postId}>{busy ? 'Making their link…' : sent ? 'Send it again' : `Send it to ${who}`}</button>
      {note && <p className="hint">{note}</p>}
    </div>
  );
}
